import React, { useEffect, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

const STATUSES = ["Sent", "Responded", "Accepted", "Rejected", "Closed"];

export default function Referrals() {
  const [clients, setClients] = useState([]);
  const [refs, setRefs] = useState([]);
  const [form, setForm] = useState({ client_id: "", specialist_name: "", specialist_type: "Psychiatrist", reason: "" });
  const [gen, setGen] = useState(false);
  const [active, setActive] = useState(null);

  const load = () => Promise.all([
    api.get("/clients").then(({ data }) => setClients(data)),
    api.get("/referrals").then(({ data }) => setRefs(data)),
  ]);
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!form.client_id || !form.specialist_name) return toast.error("Required fields missing");
    setGen(true);
    try {
      const { data } = await api.post("/referrals/generate", form);
      setRefs([data, ...refs]);
      setActive(data);
      toast.success("Referral letter generated");
    } catch { toast.error("Failed"); }
    setGen(false);
  };

  const updateStatus = async (id, status) => {
    await api.put(`/referrals/${id}`, { status });
    toast.success("Updated");
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page title="Referrals" subtitle="AI-drafted referral letters with status tracking and follow-up reminders.">
      <div className="grid lg:grid-cols-2 gap-5">
        <Card delay={0.05} testid="referral-composer">
          <h2 className="font-serif text-2xl mb-4">New Referral</h2>
          <div className="space-y-3">
            <select value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="ref-client">
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
            <input placeholder="Specialist name" value={form.specialist_name} onChange={set("specialist_name")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="ref-specialist-name" />
            <select value={form.specialist_type} onChange={set("specialist_type")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="ref-specialist-type">
              {["Psychiatrist", "Neurologist", "PCP", "Nutritionist", "Social Worker", "Occupational Therapist", "Hospital", "CMHC"].map(t => <option key={t}>{t}</option>)}
            </select>
            <textarea placeholder="Reason for referral" value={form.reason} onChange={set("reason")} rows={4} className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="ref-reason"></textarea>
            <PrimaryButton onClick={generate} disabled={gen} testid="ref-generate-btn">
              {gen ? "Drafting…" : <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Generate Letter</span>}
            </PrimaryButton>
          </div>
        </Card>

        <Card delay={0.1} testid="referral-preview">
          <h2 className="font-serif text-2xl mb-4">Preview</h2>
          {active ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Pill variant={active.status === "Accepted" ? "success" : active.status === "Rejected" ? "danger" : "warn"}>{active.status}</Pill>
                <select value={active.status} onChange={(e) => { updateStatus(active.id, e.target.value); setActive({ ...active, status: e.target.value }); }} className="text-xs px-3 py-1 rounded-full outline-none" style={{ background: "var(--mf-muted)" }} data-testid="ref-status-select">
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="p-4 rounded-xl text-sm whitespace-pre-wrap mf-prose max-h-96 overflow-y-auto" style={{ background: "var(--mf-muted)" }}>{active.letter}</div>
            </div>
          ) : (
            <div className="text-sm py-12 text-center" style={{ color: "var(--mf-text-muted)" }}>Generate a letter to preview.</div>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-2xl mb-4">All Referrals</h2>
        {refs.length === 0 ? <EmptyState icon={Send} title="No referrals yet" description="Draft your first AI-powered referral letter." /> : (
          <Card>
            <div className="space-y-2">
              {refs.map((r) => (
                <div key={r.id} className="p-3 rounded-xl flex items-center justify-between cursor-pointer hover:bg-black/[0.03]" onClick={() => setActive(r)} data-testid={`ref-item-${r.id}`} style={{ background: "var(--mf-muted)" }}>
                  <div>
                    <div className="font-medium text-sm">{r.specialist_name}</div>
                    <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{r.specialist_type} · {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <Pill variant={r.status === "Accepted" ? "success" : r.status === "Rejected" ? "danger" : "warn"}>{r.status}</Pill>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}
